import React, { useEffect, useMemo, useRef, useState } from "react";
import { useStores } from "../../../use-stores";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Backdrop,
  Box,
  Chip,
  CircularProgress,
  Grid2,
  Menu,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import { observer } from "mobx-react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import { useDebounce } from "../../../../../views/components/common/hooks/Debounce";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import { InventoryDetailsStore } from "../../../stores/InventoryDetailsStore";
import type { InventoryInwardLineInput } from "../../../stores/InventoryDetailsStore";
import InventoryDetailsItemsTable from "../../components/Inventory/InventoryDetailsItemsTable";
import InventoryDetailsPacksTable from "../../components/Inventory/InventoryDetailsPacksTable";
import PackItemsPopup from "../projects/packaging-ops/PackItemsPopup";
import AdjustItemPopup from "../projects/packaging-ops/AdjustItemPopup";
import EditItemMetaPopup from "../projects/packaging-ops/EditItemMetaPopup";
import AuditSkuDrawer from "../projects/packaging-ops/AuditSkuDrawer";
import InventoryInwardPopup from "./InventoryInwardPopup";
import AdjustImportCsvPopup from "./AdjustImportCsvPopup";
import InboundImportCsvPopup from "./InboundImportCsvPopup";
import SkuImportCsvPopup from "./SkuImportCsvPopup";
import CreatePackPopup from "../projects/packaging-ops/CreatePackPopup";
import EditPackPopup from "../projects/packaging-ops/EditPackPopup";
import EditPackItemsPopup from "../projects/packaging-ops/EditPackItemsPopup";
import ConfirmationPopup from "../../../../../views/components/common/pop-up/ConfirmationPopup";
import {
  InventoryMovementRow,
  InventoryPackItem,
  InventoryPackItemInput,
  InventoryPackSummary,
  InventoryVariant,
  InventoryVariantUpdateRequest,
} from "../../../../../models/inventory/InventoryAdmin";
import { SortType } from "../../../../../models/SortType";

type InventoryDetailsTab = "items" | "packs";

type SelectedPackItem = {
  clientSku: string;
  productName: string;
};

const InventorySwagpackDetails: React.FC = observer(() => {
  const theme = useTheme();
  const { inventoryStore, userStore } = useStores();
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const inventoryId = Number(id);
  const preferredCustomerId = Number(
    new URLSearchParams(location.search).get("customer_id") || "",
  );

  const inventoryDetailsStore = useMemo(() => new InventoryDetailsStore(), []);

  const [activeTab, setActiveTab] = useState<InventoryDetailsTab>("items");
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 400);
  const [selectedTags, setSelectedTags] = useState<FilterValue[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [downloadingCountReport, setDownloadingCountReport] = useState(false);
  const actionsAnchorRef = useRef<HTMLDivElement | null>(null);

  const [viewPackItems, setViewPackItems] =
    useState<InventoryPackSummary | null>(null);
  const [viewPackItemRows, setViewPackItemRows] = useState<InventoryPackItem[]>(
    [],
  );
  const [isViewPackItemsLoading, setIsViewPackItemsLoading] = useState(false);
  const [inwardPopupOpen, setInwardPopupOpen] = useState(false);
  const [importCsvPopupOpen, setImportCsvPopupOpen] = useState(false);
  const [inboundImportCsvPopupOpen, setInboundImportCsvPopupOpen] =
    useState(false);
  const [skuImportCsvPopupOpen, setSkuImportCsvPopupOpen] = useState(false);
  const [isInwardSubmitting, setIsInwardSubmitting] = useState(false);
  const [isImportCsvSubmitting, setIsImportCsvSubmitting] = useState(false);
  const [isInboundImportSubmitting, setIsInboundImportSubmitting] =
    useState(false);
  const [isSkuImportSubmitting, setIsSkuImportSubmitting] = useState(false);
  const [selectedItemsBySku, setSelectedItemsBySku] = useState<
    Record<string, SelectedPackItem>
  >({});
  const [createPackPopupOpen, setCreatePackPopupOpen] = useState(false);
  const [editPack, setEditPack] = useState<InventoryPackSummary | null>(null);
  const [editPackItems, setEditPackItems] =
    useState<InventoryPackSummary | null>(null);
  const [deletePack, setDeletePack] = useState<InventoryPackSummary | null>(
    null,
  );
  const [deleteSkuItem, setDeleteSkuItem] = useState<InventoryVariant | null>(
    null,
  );
  const [adjustItem, setAdjustItem] = useState<InventoryVariant | null>(null);
  const [editMetaItem, setEditMetaItem] = useState<InventoryVariant | null>(
    null,
  );
  const [auditItem, setAuditItem] = useState<InventoryVariant | null>(null);
  const [auditRows, setAuditRows] = useState<InventoryMovementRow[]>([]);
  const [isAuditLoading, setIsAuditLoading] = useState(false);

  const tagOptions: FilterValue[] = useMemo(
    () =>
      inventoryDetailsStore.customerTags
        .filter((tag) => tag.active !== 0)
        .map((tag) => ({
          id: String(tag.tag_id),
          value: tag.tag,
        })),
    [inventoryDetailsStore.customerTags],
  );

  const selectedTagIds = useMemo(
    () => new Set(selectedTags.map((tag) => String(tag.id))),
    [selectedTags],
  );

  const selectedTagIdParam = useMemo(() => {
    if (selectedTags.length === 0) return undefined;
    return selectedTags.map((tag) => String(tag.id)).join(",");
  }, [selectedTags]);

  const filteredPacks = useMemo(() => {
    if (selectedTagIds.size === 0) return inventoryDetailsStore.packs;
    return inventoryDetailsStore.packs.filter(
      (pack) => pack.tagId != null && selectedTagIds.has(String(pack.tagId)),
    );
  }, [inventoryDetailsStore.packs, selectedTagIds]);

  const itemsTotalCount = inventoryDetailsStore.variantsTotalCount;
  const packsTotalCount =
    selectedTagIds.size === 0
      ? inventoryDetailsStore.packsTotalCount
      : filteredPacks.length;
  const canManagePacks = userStore.hasPermission(EmployeeRoles.CREATE_SWAGPACK);
  const canDeleteSku = userStore.hasPermission(
    EmployeeRoles.DELETE_INVENTORY_SKU,
    true,
  );
  const selectedPackItems = useMemo(
    () => Object.values(selectedItemsBySku),
    [selectedItemsBySku],
  );
  const selectedSkus = useMemo(
    () => selectedPackItems.map((item) => item.clientSku),
    [selectedPackItems],
  );

  const getApiErrorMessage = (error: unknown, fallback: string) => {
    if (error && typeof error === "object") {
      const err = error as {
        response?: { data?: { message?: string; code?: string } };
        message?: string;
      };
      return (
        err.response?.data?.message ||
        err.response?.data?.code ||
        err.message ||
        fallback
      );
    }
    return fallback;
  };

  const loadListing = async (options?: {
    resetPage?: boolean;
    search?: string;
  }) => {
    if (!Number.isFinite(inventoryId) || inventoryId <= 0) {
      showError("Error", "Invalid inventory ID");
      return;
    }

    setIsLoading(true);
    try {
      if (options?.resetPage) {
        inventoryDetailsStore.variantsPageNo = 1;
        inventoryDetailsStore.packsPageNo = 1;
      }
      await inventoryDetailsStore.load(inventoryId, {
        search: options?.search ?? debouncedSearch,
        tagId: selectedTagIdParam,
      });
    } catch (error: unknown) {
      showError(
        "Error",
        getApiErrorMessage(error, "Failed to fetch inventory details"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const loadItemsPage = async (
    page_no: number,
    limit?: number,
    sortBy?: string,
    sortType?: SortType,
  ) => {
    if (!Number.isFinite(inventoryId) || inventoryId <= 0) return;

    setIsLoading(true);
    try {
      await inventoryDetailsStore.loadVariants(inventoryId, {
        page_no,
        limit: limit ?? inventoryDetailsStore.variantsLimit,
        ...(sortBy !== undefined ? { sortBy } : {}),
        ...(sortType !== undefined ? { sortType } : {}),
      });
    } catch (error: unknown) {
      showError(
        "Error",
        getApiErrorMessage(error, "Failed to fetch inventory items"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const loadPacksPage = async (
    page_no: number,
    limit?: number,
    sortBy?: string,
    sortType?: SortType,
  ) => {
    if (!Number.isFinite(inventoryId) || inventoryId <= 0) return;

    setIsLoading(true);
    try {
      await inventoryDetailsStore.loadPacks(inventoryId, {
        page_no,
        limit: limit ?? inventoryDetailsStore.packsLimit,
        ...(sortBy !== undefined ? { sortBy } : {}),
        ...(sortType !== undefined ? { sortType } : {}),
      });
    } catch (error: unknown) {
      showError(
        "Error",
        getApiErrorMessage(error, "Failed to fetch inventory packs"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!userStore.hasPermission(EmployeeRoles.VIEW_INVENTORY)) return;
    if (!Number.isFinite(inventoryId) || inventoryId <= 0) return;

    setSelectedTags([]);
    setSelectedItemsBySku({});

    const loadTags = async () => {
      try {
        const customerId = await inventoryDetailsStore.resolveCustomerId(
          inventoryId,
          Number.isFinite(preferredCustomerId) && preferredCustomerId > 0
            ? preferredCustomerId
            : undefined,
        );
        if (customerId) {
          await inventoryDetailsStore.loadCustomerTags(customerId);
        }
      } catch (error: unknown) {
        showError(
          "Error",
          getApiErrorMessage(error, "Failed to load inventory tags"),
        );
      }
    };

    void loadTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryId, preferredCustomerId, userStore]);

  useEffect(() => {
    if (!userStore.hasPermission(EmployeeRoles.VIEW_INVENTORY)) return;
    void loadListing({ resetPage: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryId, debouncedSearch, selectedTagIdParam, userStore]);

  const handleBulkActions = () => {
    if (actionsAnchorRef.current) {
      setAnchorEl(actionsAnchorRef.current);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const downloadCountReportCsv = async () => {
    handleClose();

    if (!Number.isFinite(inventoryId) || inventoryId <= 0) {
      showError("Error", "Invalid inventory ID");
      return;
    }

    setDownloadingCountReport(true);
    try {
      const response = await inventoryStore.downloadCountReportCsv(inventoryId);

      if (!response?.data) {
        showError("Error", "No data received for count report");
        return;
      }

      const downloadUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download =
        response.filename || `inventory-${inventoryId}-count-report.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error: unknown) {
      showError(
        "Error",
        getApiErrorMessage(error, "Failed to download count report CSV"),
      );
    } finally {
      setDownloadingCountReport(false);
    }
  };

  const handleViewPackItems = async (pack: InventoryPackSummary) => {
    try {
      setViewPackItems(pack);
      setViewPackItemRows(pack.items || []);
      if (pack.items && pack.items.length > 0) return;

      setIsViewPackItemsLoading(true);
      const items = await inventoryDetailsStore.getPackItems(pack.packId);
      setViewPackItemRows(items);
    } catch (error: unknown) {
      showError(
        "Error",
        getApiErrorMessage(
          error,
          "Failed to load pack items. Please try again later.",
        ),
      );
      setViewPackItems(null);
      setViewPackItemRows([]);
    } finally {
      setIsViewPackItemsLoading(false);
    }
  };

  const handleOpenInward = () => {
    handleClose();
    setInwardPopupOpen(true);
  };

  const handleInwardSubmit = async (lines: InventoryInwardLineInput[]) => {
    try {
      setIsInwardSubmitting(true);
      await inventoryDetailsStore.inwardVariants(inventoryId, lines);
      setInwardPopupOpen(false);
      setActiveTab("items");
    } catch (error: unknown) {
      // Rethrow so the inward popup can show the error inline.
      throw new Error(
        getApiErrorMessage(
          error,
          "Failed to inward variants. Please try again later.",
        ),
      );
    } finally {
      setIsInwardSubmitting(false);
    }
  };

  const handleAdjustSubmit = async (payload: {
    delta: number;
    note: string;
  }) => {
    setIsLoading(true);
    try {
      if (!adjustItem) return;
      await inventoryDetailsStore.adjustItem({
        inventoryId,
        clientSku: adjustItem.clientSku,
        delta: payload.delta,
        note: payload.note,
      });
      setAdjustItem(null);
    } catch (error: unknown) {
      showError(
        "Error",
        getApiErrorMessage(
          error,
          "Failed to adjust inventory. Please try again later.",
        ),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenImportCsv = () => {
    handleClose();
    if (!userStore.hasPermission(EmployeeRoles.UPDATE_INVENTORY_ITEMS_COUNT)) {
      showError(
        "Error",
        "You do not have permission to update inventory counts",
      );
      return;
    }
    if (!Number.isFinite(inventoryId) || inventoryId <= 0) {
      showError("Error", "Invalid inventory ID");
      return;
    }
    setImportCsvPopupOpen(true);
  };

  const handleImportCsvSubmit = async (payload: {
    note: string;
    csvFile: File;
  }) => {
    setIsImportCsvSubmitting(true);
    try {
      if (
        !userStore.hasPermission(EmployeeRoles.UPDATE_INVENTORY_ITEMS_COUNT)
      ) {
        showError(
          "Error",
          "You do not have permission to update inventory counts",
        );
        return;
      }
      if (!Number.isFinite(inventoryId) || inventoryId <= 0) {
        showError("Error", "Invalid inventory ID");
        return;
      }

      const result = await inventoryDetailsStore.adjustImportCsv(
        inventoryId,
        payload.note,
        payload.csvFile,
      );
      setImportCsvPopupOpen(false);
      setActiveTab("items");
      const movedCount = result?.movementIds?.length ?? 0;
      showSuccess(
        movedCount > 0
          ? `Imported ${movedCount} SKU adjustment${movedCount === 1 ? "" : "s"} from CSV.`
          : "CSV imported. All listed SKUs were already at the target quantity.",
      );
    } catch (error: unknown) {
      showError(
        "Error",
        getApiErrorMessage(
          error,
          "Failed to import inventory count CSV. Please try again later.",
        ),
      );
    } finally {
      setIsImportCsvSubmitting(false);
    }
  };

  const handleOpenInboundImportCsv = () => {
    handleClose();
    if (!userStore.hasPermission(EmployeeRoles.IMPORT_INVENTORY_ITEMS)) {
      showError(
        "Error",
        "You do not have permission to import new inventory items",
      );
      return;
    }
    if (!Number.isFinite(inventoryId) || inventoryId <= 0) {
      showError("Error", "Invalid inventory ID");
      return;
    }
    setInboundImportCsvPopupOpen(true);
  };

  const handleInboundImportCsvSubmit = async (payload: {
    note: string;
    csvFile: File;
  }) => {
    setIsInboundImportSubmitting(true);
    try {
      if (!userStore.hasPermission(EmployeeRoles.IMPORT_INVENTORY_ITEMS)) {
        showError(
          "Error",
          "You do not have permission to import new inventory items",
        );
        return;
      }
      if (!Number.isFinite(inventoryId) || inventoryId <= 0) {
        showError("Error", "Invalid inventory ID");
        return;
      }

      const result = await inventoryDetailsStore.inboundImportCsv(
        inventoryId,
        payload.note,
        payload.csvFile,
      );
      setInboundImportCsvPopupOpen(false);
      setActiveTab("items");
      const movedCount = result?.movementIds?.length ?? 0;
      showSuccess(
        movedCount > 0
          ? `Imported ${movedCount} new SKU${movedCount === 1 ? "" : "s"} from CSV.`
          : "CSV imported. No new SKUs were created.",
      );
    } catch (error: unknown) {
      showError(
        "Error",
        getApiErrorMessage(
          error,
          "Failed to import new inventory items CSV. Please try again later.",
        ),
      );
    } finally {
      setIsInboundImportSubmitting(false);
    }
  };

  const handleOpenSkuImportCsv = () => {
    handleClose();
    if (!userStore.hasPermission(EmployeeRoles.UPDATE_INVENTORY_SKU)) {
      showError("Error", "You do not have permission to update inventory SKUs");
      return;
    }
    if (!Number.isFinite(inventoryId) || inventoryId <= 0) {
      showError("Error", "Invalid inventory ID");
      return;
    }
    setSkuImportCsvPopupOpen(true);
  };

  const handleSkuImportCsvSubmit = async (payload: {
    note: string;
    csvFile: File;
  }) => {
    setIsSkuImportSubmitting(true);
    try {
      if (!userStore.hasPermission(EmployeeRoles.UPDATE_INVENTORY_SKU)) {
        showError(
          "Error",
          "You do not have permission to update inventory SKUs",
        );
        return;
      }
      if (!Number.isFinite(inventoryId) || inventoryId <= 0) {
        showError("Error", "Invalid inventory ID");
        return;
      }

      const result = await inventoryDetailsStore.importSkuCsv(
        inventoryId,
        payload.note,
        payload.csvFile,
      );
      setSkuImportCsvPopupOpen(false);
      setActiveTab("items");
      const updatedCount = result?.updatedCount ?? 0;
      const renamedCount = result?.lines?.length ?? 0;
      const thresholdCount = result?.thresholdUpdates?.length ?? 0;
      showSuccess(
        updatedCount > 0
          ? `Updated ${updatedCount} SKU${updatedCount === 1 ? "" : "s"} from CSV (${renamedCount} renamed, ${thresholdCount} min threshold change${thresholdCount === 1 ? "" : "s"}).`
          : "CSV imported. No SKUs were changed.",
      );
    } catch (error: unknown) {
      showError(
        "Error",
        getApiErrorMessage(
          error,
          "Failed to import SKU update CSV. Please try again later.",
        ),
      );
    } finally {
      setIsSkuImportSubmitting(false);
    }
  };

  const handleToggleSelectItem = (row: InventoryVariant) => {
    try {
      const sku = row.clientSku?.trim();
      if (!sku) return;
      setSelectedItemsBySku((current) => {
        if (current[sku]) {
          const next = { ...current };
          delete next[sku];
          return next;
        }
        return {
          ...current,
          [sku]: {
            clientSku: sku,
            productName: row.productName || "",
          },
        };
      });
    } catch (error: unknown) {
      showError(
        "Error",
        getApiErrorMessage(error, "Failed to update SKU selection"),
      );
    }
  };

  const handleToggleSelectAllItems = () => {
    try {
      const pageRows = inventoryDetailsStore.variants;
      const allPageSelected =
        pageRows.length > 0 &&
        pageRows.every((row) => selectedItemsBySku[row.clientSku]);

      setSelectedItemsBySku((current) => {
        const next = { ...current };
        if (allPageSelected) {
          pageRows.forEach((row) => {
            delete next[row.clientSku];
          });
          return next;
        }
        pageRows.forEach((row) => {
          const sku = row.clientSku?.trim();
          if (!sku) return;
          next[sku] = {
            clientSku: sku,
            productName: row.productName || current[sku]?.productName || "",
          };
        });
        return next;
      });
    } catch (error: unknown) {
      showError(
        "Error",
        getApiErrorMessage(error, "Failed to update SKU selection"),
      );
    }
  };

  const handleRemoveSelectedItem = (clientSku: string) => {
    try {
      setSelectedItemsBySku((current) => {
        if (!current[clientSku]) return current;
        const next = { ...current };
        delete next[clientSku];
        return next;
      });
    } catch (error: unknown) {
      showError(
        "Error",
        getApiErrorMessage(error, "Failed to remove selected SKU"),
      );
    }
  };

  const handleOpenCreatePack = () => {
    try {
      if (!canManagePacks) {
        showError("Error", "You do not have permission to create packs");
        return;
      }
      if (selectedPackItems.length === 0) {
        showError("Error", "Select at least one SKU to create a pack");
        return;
      }
      setCreatePackPopupOpen(true);
    } catch (error: unknown) {
      showError(
        "Error",
        getApiErrorMessage(error, "Failed to open create pack"),
      );
    }
  };

  const handleCreatePackSubmit = async (payload: {
    label: string;
    plannedQty: number;
    items: Array<{ clientSku: string; qty: number }>;
  }) => {
    setIsLoading(true);
    try {
      await inventoryDetailsStore.createPack(payload);
      setCreatePackPopupOpen(false);
      setSelectedItemsBySku({});
      setActiveTab("packs");
    } catch (error: unknown) {
      showError(
        "Error",
        getApiErrorMessage(
          error,
          "Failed to create pack. Please try again later.",
        ),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditPackSubmit = async (payload: {
    label: string;
    plannedQty: number;
    description?: string;
  }) => {
    if (!editPack) return;
    setIsLoading(true);
    try {
      await inventoryDetailsStore.patchPack(editPack.packId, payload);
      setEditPack(null);
    } catch (error: unknown) {
      showError(
        "Error",
        getApiErrorMessage(
          error,
          "Failed to update pack. Please try again later.",
        ),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePackConfirm = async () => {
    if (!deletePack) return;
    setIsLoading(true);
    try {
      await inventoryDetailsStore.deletePack(deletePack.packId);
      setDeletePack(null);
    } catch (error: unknown) {
      showError(
        "Error",
        getApiErrorMessage(
          error,
          "Failed to delete pack. Please try again later.",
        ),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditPackItemsSubmit = async (items: InventoryPackItemInput[]) => {
    if (!editPackItems) return;
    setIsLoading(true);
    try {
      await inventoryDetailsStore.replacePackItems(editPackItems.packId, items);
      setEditPackItems(null);
    } catch (error: unknown) {
      showError(
        "Error",
        getApiErrorMessage(
          error,
          "Failed to update pack items. Please try again later.",
        ),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSkuConfirm = async () => {
    if (!deleteSkuItem) return;
    if (!canDeleteSku) {
      showError("Error", "You do not have permission to delete inventory SKUs");
      return;
    }
    setIsLoading(true);
    try {
      await inventoryDetailsStore.deleteVariantBySku(deleteSkuItem.clientSku);
      setSelectedItemsBySku((current) => {
        if (!current[deleteSkuItem.clientSku]) return current;
        const next = { ...current };
        delete next[deleteSkuItem.clientSku];
        return next;
      });
      setDeleteSkuItem(null);
    } catch (error: unknown) {
      showError(
        "Error",
        getApiErrorMessage(
          error,
          "Failed to delete SKU. Zero out on-hand and reserved via Adjust first.",
        ),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditMetaSubmit = async (
    payload: InventoryVariantUpdateRequest,
  ) => {
    try {
      if (!editMetaItem?.id) return;
      setIsLoading(true);
      await inventoryDetailsStore.updateVariantMeta(editMetaItem.id, payload);
      setEditMetaItem(null);
    } catch (error: unknown) {
      showError(
        "Error",
        getApiErrorMessage(
          error,
          "Failed to update item meta. Please try again later.",
        ),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenAudit = async (row: InventoryVariant) => {
    try {
      setAuditItem(row);
      setAuditRows([]);
      setIsAuditLoading(true);
      const rows = await inventoryDetailsStore.getSkuMovements(row.clientSku);
      setAuditRows(rows);
    } catch (error: unknown) {
      showError(
        "Error",
        getApiErrorMessage(
          error,
          "Failed to load audit ledger. Please try again later.",
        ),
      );
      setAuditItem(null);
    } finally {
      setIsAuditLoading(false);
    }
  };

  const handleCreateTag = inventoryDetailsStore.customerId
    ? (tagId: string, tag: string) =>
        inventoryDetailsStore.createCustomerTag(
          Number(inventoryDetailsStore.customerId),
          tagId,
          tag,
        )
    : undefined;

  const handleRefresh = () => {
    void loadListing();
  };

  const handleSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    try {
      if (event.key !== "Enter") return;
      event.preventDefault();
      void loadListing({ resetPage: true, search: searchTerm });
    } catch (error: unknown) {
      showError("Error", getApiErrorMessage(error, "Failed to apply search"));
    }
  };

  const handleItemsPageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => {
    void loadItemsPage(page);
  };

  const handleItemsRowsPerPageChange = (limit: number) => {
    void loadItemsPage(1, limit);
  };

  const handleItemsSortChange = (column: string, nextSortType: SortType) => {
    try {
      void loadItemsPage(
        1,
        inventoryDetailsStore.variantsLimit,
        column,
        nextSortType,
      );
    } catch (error: unknown) {
      showError(
        "Error",
        getApiErrorMessage(error, "Failed to sort inventory items"),
      );
    }
  };

  const handlePacksPageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => {
    void loadPacksPage(page);
  };

  const handlePacksRowsPerPageChange = (limit: number) => {
    void loadPacksPage(1, limit);
  };

  const handlePacksSortChange = (column: string, nextSortType: SortType) => {
    try {
      void loadPacksPage(
        1,
        inventoryDetailsStore.packsLimit,
        column,
        nextSortType,
      );
    } catch (error: unknown) {
      showError(
        "Error",
        getApiErrorMessage(error, "Failed to sort inventory packs"),
      );
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          cursor: "pointer",
          color: theme.palette.customColors.greyTextColor,
          "&:hover": {
            color: theme.palette.customColors.greyTextColor,
          },
          "&:active": {
            color: theme.palette.customColors.greyTextColor,
          },
          mb: 2,
          ml: 2,
          maxWidth: "60px",
        }}
        onClick={() => {
          navigate(-1);
        }}
      >
        <ArrowBackIcon sx={{ fontSize: "16px" }} />
        <span style={{ fontSize: "14px", fontWeight: "600" }}>Back</span>
      </Box>

      {!userStore.hasPermission(EmployeeRoles.VIEW_INVENTORY) ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="10rem"
          textAlign="center"
        >
          <Typography variant="h6" fontWeight="500" color="text.secondary">
            You don't have permission to view this section.
          </Typography>
        </Box>
      ) : (
        <Paper
          sx={{
            padding: 5,
            paddingBottom: 3,
            paddingTop: 3,
            position: "relative",
          }}
        >
          <Backdrop
            open={
              isLoading &&
              !inwardPopupOpen &&
              !importCsvPopupOpen &&
              !inboundImportCsvPopupOpen &&
              !skuImportCsvPopupOpen &&
              !createPackPopupOpen &&
              !editPack &&
              !editPackItems &&
              !deletePack &&
              !deleteSkuItem &&
              !adjustItem
            }
            sx={{
              position: "absolute",
              zIndex: (t) => t.zIndex.drawer + 1,
              color: theme.palette.customColors?.white,
              backgroundColor: "rgba(255,255,255,0.55)",
            }}
          >
            <CircularProgress color="inherit" />
          </Backdrop>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box display="flex" flexDirection="column" gap={0.5}>
              <CustomTypography variant="heading3">
                Inventory Details
              </CustomTypography>
              <CustomTypography
                variant="body2"
                color={theme.palette.customColors?.greyTextColor}
              >
                Inventory ID: {inventoryId}
              </CustomTypography>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: { xs: "flex-start", md: "flex-end" },
                alignItems: "center",
                gap: 1,
              }}
            >
              <AppButton
                variant="grayscale"
                label="Refresh"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                customStyles={{ minWidth: 120 }}
              />
              <Box ref={actionsAnchorRef}>
                <AppButton
                  variant="grayscale"
                  label="Actions"
                  onClick={handleBulkActions}
                  disabled={downloadingCountReport}
                  customStyles={{ minWidth: 180 }}
                />
              </Box>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                disableAutoFocusItem
                slotProps={{
                  paper: {
                    sx: {
                      mt: 1,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      boxShadow:
                        "0px 20px 45px rgba(15, 23, 42, 0.15), 0px 6px 18px rgba(15, 23, 42, 0.08)",
                      minWidth: 240,
                      overflow: "hidden",
                    },
                  },
                }}
              >
                <MenuItem
                  onClick={handleOpenInward}
                  sx={{
                    fontSize: "0.9rem",
                    px: 2,
                    py: 1.25,
                    gap: 1,
                    "&:hover": {
                      backgroundColor:
                        theme.palette.customColors?.lightPurple ||
                        theme.palette.action.hover,
                    },
                  }}
                >
                  <CustomTypography variant="body2" fontWeight={600}>
                    Inward Items
                  </CustomTypography>
                </MenuItem>
                <MenuItem
                  onClick={downloadCountReportCsv}
                  disabled={downloadingCountReport}
                  sx={{
                    fontSize: "0.9rem",
                    px: 2,
                    py: 1.25,
                    gap: 1,
                    "&:hover": {
                      backgroundColor:
                        theme.palette.customColors?.lightPurple ||
                        theme.palette.action.hover,
                    },
                  }}
                >
                  <CustomTypography variant="body2" fontWeight={600}>
                    {downloadingCountReport
                      ? "Downloading..."
                      : "Download Items Count Report"}
                  </CustomTypography>
                </MenuItem>
                <MenuItem
                  onClick={handleOpenInboundImportCsv}
                  disabled={
                    !userStore.hasPermission(
                      EmployeeRoles.IMPORT_INVENTORY_ITEMS,
                    )
                  }
                  sx={{
                    fontSize: "0.9rem",
                    px: 2,
                    py: 1.25,
                    gap: 1,
                    "&:hover": {
                      backgroundColor:
                        theme.palette.customColors?.lightPurple ||
                        theme.palette.action.hover,
                    },
                    "&.Mui-disabled": { opacity: 0.4 },
                  }}
                >
                  <CustomTypography variant="body2" fontWeight={600}>
                    Import Items via CSV
                  </CustomTypography>
                </MenuItem>
                <MenuItem
                  onClick={handleOpenImportCsv}
                  disabled={
                    !userStore.hasPermission(
                      EmployeeRoles.UPDATE_INVENTORY_ITEMS_COUNT,
                    )
                  }
                  sx={{
                    fontSize: "0.9rem",
                    px: 2,
                    py: 1.25,
                    gap: 1,
                    "&:hover": {
                      backgroundColor:
                        theme.palette.customColors?.lightPurple ||
                        theme.palette.action.hover,
                    },
                    "&.Mui-disabled": { opacity: 0.4 },
                  }}
                >
                  <CustomTypography variant="body2" fontWeight={600}>
                    Adjust Items via CSV
                  </CustomTypography>
                </MenuItem>
                <MenuItem
                  onClick={handleOpenSkuImportCsv}
                  disabled={
                    !userStore.hasPermission(EmployeeRoles.UPDATE_INVENTORY_SKU)
                  }
                  sx={{
                    fontSize: "0.9rem",
                    px: 2,
                    py: 1.25,
                    gap: 1,
                    "&:hover": {
                      backgroundColor:
                        theme.palette.customColors?.lightPurple ||
                        theme.palette.action.hover,
                    },
                    "&.Mui-disabled": { opacity: 0.4 },
                  }}
                >
                  <CustomTypography variant="body2" fontWeight={600}>
                    Update SKUs via CSV
                  </CustomTypography>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleClose();
                    if (!Number.isFinite(inventoryId) || inventoryId <= 0) {
                      showError("Error", "Invalid inventory ID");
                      return;
                    }
                    navigate(
                      `/inventory/count-import-logs?inventoryId=${inventoryId}`,
                    );
                  }}
                  sx={{
                    fontSize: "0.9rem",
                    px: 2,
                    py: 1.25,
                    gap: 1,
                    "&:hover": {
                      backgroundColor:
                        theme.palette.customColors?.lightPurple ||
                        theme.palette.action.hover,
                    },
                  }}
                >
                  <CustomTypography variant="body2" fontWeight={600}>
                    Audit Adjust CSV Updates
                  </CustomTypography>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleClose();
                    if (!Number.isFinite(inventoryId) || inventoryId <= 0) {
                      showError("Error", "Invalid inventory ID");
                      return;
                    }
                    navigate(
                      `/inventory/inbound-import-logs?inventoryId=${inventoryId}`,
                    );
                  }}
                  sx={{
                    fontSize: "0.9rem",
                    px: 2,
                    py: 1.25,
                    gap: 1,
                    "&:hover": {
                      backgroundColor:
                        theme.palette.customColors?.lightPurple ||
                        theme.palette.action.hover,
                    },
                  }}
                >
                  <CustomTypography variant="body2" fontWeight={600}>
                    Audit Import CSV Updates
                  </CustomTypography>
                </MenuItem>
              </Menu>
            </Box>
          </Box>

          <Grid2 container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid2 size={{ xs: 12, md: canManagePacks ? 6 : 8 }}>
              <CustomTextField
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value || "")}
                onKeyDown={handleSearchKeyDown}
                width="100%"
                placeholder={
                  activeTab === "items"
                    ? "Search by SKU, product name, or product ID"
                    : "Search by pack name or ID"
                }
                customStyles={{
                  backgroundColor:
                    theme?.palette?.customColors?.grayBackGroundColor,
                }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: canManagePacks ? 3 : 4 }}>
              <AppSearchDropdownMulti
                title="Filter by tag"
                isMultiSelect
                local
                list={tagOptions}
                value={selectedTags}
                disabled={tagOptions.length === 0}
                handleSelect={(items) => setSelectedTags(items)}
                minWidth="100%"
                backgroundColor={
                  theme?.palette?.customColors?.grayBackGroundColor
                }
              />
            </Grid2>
            {canManagePacks && (
              <Grid2 size={{ xs: 12, md: 3 }}>
                <AppButton
                  label="Create Pack"
                  variant="grayscale"
                  startIcon={<AddIcon />}
                  onClick={handleOpenCreatePack}
                  disabled={selectedPackItems.length === 0}
                  customStyles={{
                    minWidth: "100%",
                    height: "38px",
                    color: theme?.palette?.customColors?.blackBorder,
                  }}
                />
              </Grid2>
            )}
          </Grid2>

          {canManagePacks && selectedPackItems.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${theme.palette.customColors?.greyBorder}`,
                backgroundColor:
                  theme.palette.customColors?.grayBackGroundColor,
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
                flexWrap="wrap"
                mb={1}
              >
                <CustomTypography variant="body2" fontWeight={600}>
                  SELECTED SKUS ({selectedPackItems.length})
                </CustomTypography>
                <AppButton
                  label="Clear selection"
                  variant="grayscale"
                  onClick={() => setSelectedItemsBySku({})}
                  customStyles={{ minWidth: 140, height: "32px" }}
                />
              </Box>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {selectedPackItems.map((item) => (
                  <Chip
                    key={item.clientSku}
                    label={
                      item.productName
                        ? `${item.productName} (${item.clientSku})`
                        : item.clientSku
                    }
                    onDelete={() => handleRemoveSelectedItem(item.clientSku)}
                    sx={{
                      maxWidth: "100%",
                      backgroundColor: theme.palette.customColors?.white,
                      fontWeight: 600,
                    }}
                  />
                ))}
              </Box>
            </Paper>
          )}

          <Tabs
            value={activeTab}
            onChange={(_, value: InventoryDetailsTab) => setActiveTab(value)}
            sx={{
              mb: 0,
              ".MuiTabs-indicator": {
                backgroundColor: theme.palette.customColors?.purple,
              },
            }}
          >
            <Tab
              value="items"
              label={`ITEMS (${itemsTotalCount})`}
              sx={{ textTransform: "none", fontWeight: 600 }}
            />
            <Tab
              value="packs"
              label={`PACKS (${packsTotalCount})`}
              sx={{ textTransform: "none", fontWeight: 600 }}
            />
          </Tabs>

          {activeTab === "items" ? (
            <InventoryDetailsItemsTable
              rows={inventoryDetailsStore.variants}
              isLoading={isLoading}
              page_no={inventoryDetailsStore.variantsPageNo}
              limit={inventoryDetailsStore.variantsLimit}
              total_count={itemsTotalCount}
              sortBy={inventoryDetailsStore.variantsSortBy}
              sortType={inventoryDetailsStore.variantsSortType}
              onPageChange={handleItemsPageChange}
              onRowsPerPageChange={handleItemsRowsPerPageChange}
              onSortChange={handleItemsSortChange}
              selectedSkus={selectedSkus}
              canSelectRows={canManagePacks}
              onToggleSelect={handleToggleSelectItem}
              onToggleSelectAll={handleToggleSelectAllItems}
              onProductClick={(productId) => {
                navigate(`/products/${productId}`);
              }}
              onAdjustRow={(row) => setAdjustItem(row)}
              onEditMetaRow={(row) => setEditMetaItem(row)}
              onAuditRow={(row) => {
                void handleOpenAudit(row);
              }}
              canDeleteSku={canDeleteSku}
              onDeleteRow={(row) => setDeleteSkuItem(row)}
            />
          ) : (
            <InventoryDetailsPacksTable
              packs={filteredPacks}
              isLoading={isLoading}
              page_no={inventoryDetailsStore.packsPageNo}
              limit={inventoryDetailsStore.packsLimit}
              total_count={packsTotalCount}
              sortBy={inventoryDetailsStore.packsSortBy}
              sortType={inventoryDetailsStore.packsSortType}
              onPageChange={handlePacksPageChange}
              onRowsPerPageChange={handlePacksRowsPerPageChange}
              onSortChange={handlePacksSortChange}
              canManagePacks={canManagePacks}
              onEditPack={(pack) => setEditPack(pack)}
              onEditPackItems={(pack) => setEditPackItems(pack)}
              onDeletePack={(pack) => setDeletePack(pack)}
              onViewItems={(pack) => {
                void handleViewPackItems(pack);
              }}
            />
          )}

          <PackItemsPopup
            open={!!viewPackItems}
            pack={viewPackItems}
            items={viewPackItemRows}
            isLoading={isViewPackItemsLoading}
            onClose={() => {
              setViewPackItems(null);
              setViewPackItemRows([]);
            }}
            onProductClick={(productId) => {
              navigate(`/products/${productId}`);
            }}
          />

          <InventoryInwardPopup
            open={inwardPopupOpen}
            tags={inventoryDetailsStore.customerTags}
            isSubmitting={isInwardSubmitting}
            onClose={() => {
              if (isInwardSubmitting) return;
              setInwardPopupOpen(false);
            }}
            onSearchSku={inventoryDetailsStore.searchProductsBySku}
            onFetchVariants={inventoryDetailsStore.fetchProductVariants}
            onCreateTag={handleCreateTag}
            onSubmit={async (lines) => {
              await handleInwardSubmit(lines);
            }}
          />

          <AdjustItemPopup
            open={!!adjustItem}
            item={adjustItem}
            isSubmitting={isLoading && !!adjustItem}
            onClose={() => setAdjustItem(null)}
            onSubmit={async (payload) => {
              await handleAdjustSubmit(payload);
            }}
          />

          <AdjustImportCsvPopup
            open={importCsvPopupOpen}
            inventoryId={inventoryId}
            isSubmitting={isImportCsvSubmitting}
            onClose={() => {
              if (isImportCsvSubmitting) return;
              setImportCsvPopupOpen(false);
            }}
            onSubmit={async (payload) => {
              await handleImportCsvSubmit(payload);
            }}
          />

          <InboundImportCsvPopup
            open={inboundImportCsvPopupOpen}
            inventoryId={inventoryId}
            isSubmitting={isInboundImportSubmitting}
            onClose={() => {
              if (isInboundImportSubmitting) return;
              setInboundImportCsvPopupOpen(false);
            }}
            onSubmit={async (payload) => {
              await handleInboundImportCsvSubmit(payload);
            }}
          />

          <SkuImportCsvPopup
            open={skuImportCsvPopupOpen}
            inventoryId={inventoryId}
            isSubmitting={isSkuImportSubmitting}
            onClose={() => {
              if (isSkuImportSubmitting) return;
              setSkuImportCsvPopupOpen(false);
            }}
            onSubmit={async (payload) => {
              await handleSkuImportCsvSubmit(payload);
            }}
          />

          <EditItemMetaPopup
            open={!!editMetaItem}
            item={editMetaItem}
            tags={inventoryDetailsStore.customerTags}
            onCreateTag={handleCreateTag}
            onClose={() => setEditMetaItem(null)}
            onSubmit={(payload) => {
              void handleEditMetaSubmit(payload);
            }}
          />

          <CreatePackPopup
            open={createPackPopupOpen}
            items={selectedPackItems}
            onClose={() => {
              if (isLoading) return;
              setCreatePackPopupOpen(false);
            }}
            onSubmit={(payload) => {
              void handleCreatePackSubmit(payload);
            }}
          />

          <EditPackPopup
            open={!!editPack}
            pack={editPack}
            onClose={() => {
              if (isLoading) return;
              setEditPack(null);
            }}
            onSubmit={(payload) => {
              void handleEditPackSubmit(payload);
            }}
          />

          <EditPackItemsPopup
            open={!!editPackItems}
            pack={editPackItems}
            isSubmitting={isLoading && !!editPackItems}
            onClose={() => {
              if (isLoading) return;
              setEditPackItems(null);
            }}
            onSearchVariants={(search) =>
              inventoryDetailsStore.searchVariants(search)
            }
            onLoadPackItems={(packId) =>
              inventoryDetailsStore.getPackItems(packId)
            }
            onSubmit={async (items) => {
              await handleEditPackItemsSubmit(items);
            }}
          />

          {deletePack && (
            <ConfirmationPopup
              open={!!deletePack}
              id={deletePack.packId}
              title="Delete Pack"
              content={`Are you sure you want to delete pack "${deletePack.label}"? This will mark the pack as inactive.`}
              zIndex={2100}
              onConfirm={() => {
                void handleDeletePackConfirm();
              }}
              onCancel={() => setDeletePack(null)}
            />
          )}

          {deleteSkuItem && (
            <ConfirmationPopup
              open={!!deleteSkuItem}
              id={deleteSkuItem.id || 0}
              title="Delete SKU"
              content={`Are you sure you want to deactivate SKU "${deleteSkuItem.clientSku}"? This frees it for re-registration. History is retained. On-hand and reserved must already be zero.`}
              zIndex={2100}
              onConfirm={() => {
                void handleDeleteSkuConfirm();
              }}
              onCancel={() => setDeleteSkuItem(null)}
            />
          )}

          <AuditSkuDrawer
            open={!!auditItem}
            clientSku={auditItem?.clientSku || null}
            productName={auditItem?.productName}
            rows={auditRows}
            isLoading={isAuditLoading}
            onClose={() => {
              setAuditItem(null);
              setAuditRows([]);
            }}
          />
        </Paper>
      )}
    </Box>
  );
});

export default InventorySwagpackDetails;
